import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, ArrowDownLeft, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { money, fmtClabe, isValidClabe } from "@/lib/format";

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>;

const ClabeInput = ({ value, onChange, banks, testId }) => {
  const ok = isValidClabe(value);
  const bank = banks.find((b) => b.code === value.slice(0, 3));
  return (
    <div>
      <Input data-testid={testId} className="font-mono tracking-wider" maxLength={18} inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} placeholder="18 dígitos" />
      {value.length > 0 && (
        <p className={`mt-1 flex items-center gap-1 text-xs ${ok ? "text-teal-700" : "text-slate-500"}`} data-testid={`${testId}-status`}>
          {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {ok ? `CLABE válida · ${bank?.name || "Otro banco"}` : `${value.length}/18 · ${value.length === 18 ? "dígito verificador inválido" : "incompleta"}`}
        </p>
      )}
    </div>
  );
};

const SendForm = ({ accounts, banks, onDone }) => {
  const api = useApi();
  const empty = { account_id: "", clabe_destino: "", beneficiario: "", rfc_beneficiario: "", monto: "", concepto: "", referencia: "" };
  const [f, setF] = useState(empty);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const acc = accounts.find((a) => a.id === f.account_id);
  const valid = acc && isValidClabe(f.clabe_destino) && f.beneficiario.length > 1 && Number(f.monto) > 0 && f.concepto;
  const send = async (ev) => {
    ev.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/transfers", { ...f, monto: Number(f.monto), referencia: f.referencia ? Number(f.referencia) : null });
      toast.success(`SPEI enviado · Rastreo ${r.data.clave_rastreo}`);
      setF(empty); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setConfirm(false); }
  };
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <div className="md:col-span-2"><F label="Cuenta origen e-Plata">
        <Select value={f.account_id} onValueChange={set("account_id")}><SelectTrigger data-testid="transfer-origin-select"><SelectValue placeholder="Selecciona cuenta" /></SelectTrigger>
          <SelectContent>{accounts.filter((a) => a.estado === "activa").map((a) => <SelectItem key={a.id} value={a.id}>{a.client_nombre} · {fmtClabe(a.clabe)} · {money(a.saldo)}</SelectItem>)}</SelectContent></Select>
      </F></div>
      <F label="CLABE destino"><ClabeInput value={f.clabe_destino} onChange={set("clabe_destino")} banks={banks} testId="input-clabe-destino" /></F>
      <F label="Nombre del beneficiario"><Input value={f.beneficiario} onChange={(e) => set("beneficiario")(e.target.value)} data-testid="input-beneficiario" /></F>
      <F label="RFC / CURP beneficiario (opcional)"><Input value={f.rfc_beneficiario} onChange={(e) => set("rfc_beneficiario")(e.target.value.toUpperCase())} data-testid="input-rfc-beneficiario" /></F>
      <F label="Monto (MXN)"><Input type="number" min="0" step="0.01" className="font-mono" value={f.monto} onChange={(e) => set("monto")(e.target.value)} data-testid="input-monto" /></F>
      <F label="Concepto de pago (máx. 39)"><Input maxLength={39} value={f.concepto} onChange={(e) => set("concepto")(e.target.value)} data-testid="input-concepto" /></F>
      <F label="Referencia numérica (opcional, 7 dígitos)"><Input maxLength={7} inputMode="numeric" className="font-mono" value={f.referencia} onChange={(e) => set("referencia")(e.target.value.replace(/\D/g, ""))} data-testid="input-referencia" /></F>
      <div className="md:col-span-2"><Button className="bg-teal-600 hover:bg-teal-700" disabled={!valid} onClick={() => setConfirm(true)} data-testid="transfer-submit-btn"><Send className="mr-2 h-4 w-4" />Revisar y enviar</Button></div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="transfer-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Confirmar SPEI</AlertDialogTitle>
            <AlertDialogDescription asChild><div className="space-y-1 text-sm text-slate-600">
              <p>Enviar <b className="text-slate-900">{money(f.monto)}</b> a <b className="text-slate-900">{f.beneficiario}</b></p>
              <p className="font-mono">{fmtClabe(f.clabe_destino)}</p><p>Desde {acc?.client_nombre} ({fmtClabe(acc?.clabe)})</p>
            </div></AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-teal-600 hover:bg-teal-700" onClick={send} disabled={busy} data-testid="transfer-confirm-btn">{busy ? "Enviando…" : "Enviar"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const DepositForm = ({ accounts, banks, onDone }) => {
  const api = useApi();
  const empty = { clabe: "", monto: "", ordenante: "", clabe_ordenante: "", concepto: "" };
  const [f, setF] = useState(empty);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const go = async () => {
    try { await api.post("/stp/simulate-deposit", { ...f, monto: Number(f.monto) }); toast.success("Depósito SPEI recibido"); setF(empty); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <div className="md:col-span-2"><F label="Cuenta e-Plata que recibe">
        <Select value={f.clabe} onValueChange={set("clabe")}><SelectTrigger data-testid="deposit-account-select"><SelectValue placeholder="Selecciona cuenta" /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.clabe}>{a.client_nombre} · {fmtClabe(a.clabe)}</SelectItem>)}</SelectContent></Select>
      </F></div>
      <F label="Nombre del ordenante"><Input value={f.ordenante} onChange={(e) => set("ordenante")(e.target.value)} data-testid="deposit-ordenante" /></F>
      <F label="CLABE ordenante"><ClabeInput value={f.clabe_ordenante} onChange={set("clabe_ordenante")} banks={banks} testId="deposit-clabe-ordenante" /></F>
      <F label="Monto (MXN)"><Input type="number" min="0" step="0.01" className="font-mono" value={f.monto} onChange={(e) => set("monto")(e.target.value)} data-testid="deposit-monto" /></F>
      <F label="Concepto"><Input maxLength={39} value={f.concepto} onChange={(e) => set("concepto")(e.target.value)} data-testid="deposit-concepto" /></F>
      <div className="md:col-span-2"><Button className="bg-slate-900 hover:bg-slate-800" onClick={go} disabled={!f.clabe || !isValidClabe(f.clabe_ordenante) || !(Number(f.monto) > 0) || !f.ordenante || !f.concepto} data-testid="deposit-submit-btn"><ArrowDownLeft className="mr-2 h-4 w-4" />Registrar depósito</Button></div>
    </div>
  );
};

export default function Transfers() {
  const api = useApi();
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/accounts").then((r) => setAccounts(r.data)); }, [api, tick]);
  useEffect(() => { api.get("/banks").then((r) => setBanks(r.data)); }, [api]);
  return (
    <>
      <PageHeader title="SPEI vía STP" subtitle="Envío de transferencias y recepción de depósitos" />
      <Tabs defaultValue="enviar">
        <TabsList className="bg-white shadow-sm">
          <TabsTrigger value="enviar" data-testid="tab-enviar" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">Enviar SPEI</TabsTrigger>
          <TabsTrigger value="recibir" data-testid="tab-recibir" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">Simular depósito entrante</TabsTrigger>
        </TabsList>
        <TabsContent value="enviar" className="mt-6 rounded-2xl border border-slate-200 bg-white p-6"><SendForm accounts={accounts} banks={banks} onDone={() => setTick(tick + 1)} /></TabsContent>
        <TabsContent value="recibir" className="mt-6 rounded-2xl border border-slate-200 bg-white p-6"><DepositForm accounts={accounts} banks={banks} onDone={() => setTick(tick + 1)} /></TabsContent>
      </Tabs>
    </>
  );
}
